import React, { useState } from 'react'
import { ChevronRight } from "lucide-react";
import { useEditorStore } from '../../stores/editorStore'
import PropertyField from './PropertyField'

interface PropertyPanelProps {
  onToggleSidebar?: () => void;
}

export default function PropertyPanel({ onToggleSidebar }: PropertyPanelProps) {
  const { components, selectedComponent, updateComponentProp, previewMode } = useEditorStore()
  const [isClicked, setIsClicked] = useState(false)
  const getComponentId = (component: any): string => component?.name_id || component?.name || ''

  // Encontrar o componente selecionado
  const component = components.find(c => getComponentId(c) === selectedComponent)

  if (!component) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Propriedades</h3>
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Ocultar painel de propriedades"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 p-4 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">
              Nenhum componente selecionado
            </p>
            <p className="text-xs text-gray-400">
              Clique em um componente para editar suas propriedades
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePropertyChange = (propName: string, value: any) => {
    // Atualiza o estado local sem salvar automaticamente
    updateComponentProp(getComponentId(component), propName, value)
  }

  const handleApplyChanges = async () => {
    setIsClicked(true)
    
    try {
      const { components } = useEditorStore.getState()
      // Aplica e atualiza preview imediatamente via store helper
      await useEditorStore.getState().applyAndRefreshPreview(components)
      console.log('✅ Mudanças aplicadas com sucesso!')
    } catch (error) {
      console.error('❌ Erro ao aplicar mudanças:', error)
    } finally {
      // Reset do estado após um breve delay para mostrar o feedback
      setTimeout(() => setIsClicked(false), 600)
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col">
            <h3 className="font-semibold text-gray-900">Propriedades</h3>
            <span className="text-sm text-gray-500">{component.label}</span>
            <span className="text-xs text-gray-600">
              {component.type || component.component}
            </span>
          </div>
          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Ocultar painel de propriedades"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        {/* Informações do Componente */}
        <div className="mb-6">
          <div className="space-y-3">
            {component.description && (
              <span className="text-xs text-gray-600 mb-1">
                {component.description}
              </span>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Label
              </label>
              <input
                type="text"
                value={component.label || ""}
                onChange={(e) => {
                  const { components } = useEditorStore.getState();
                  const updated = components.map((c) =>
                    getComponentId(c) === getComponentId(component)
                      ? { ...c, label: e.target.value }
                      : c
                  );
                  useEditorStore.getState().setComponents(updated);
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs"
                placeholder="Nome do componente"
              />
            </div>

            {component.position && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Posição
                </label>
                <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-600">
                  {component.position}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Propriedades */}
        {component.props && Object.keys(component.props).length > 0 && (
          <div>
            <div className="space-y-2">
              {Object.entries(component.props).map(([propName, prop]: [string, any]) => {
                // Buscar o valor correspondente no component.values
                const currentValue = component.values?.[propName]
                const propType = prop?.type

                if (propType === 'object') {
                  const propWithValue = {
                    ...prop,
                    value: currentValue || {}
                  }
                  
                  return (
                    <PropertyField
                      key={`${propName}-${previewMode}`}
                      propName={propName}
                      prop={propWithValue}
                      sectionName={getComponentId(component)}
                      onChange={(value) => handlePropertyChange(propName, value)}
                    />
                  )
                }

                if (propType === 'array') {
                  const propWithValue = {
                    ...prop,
                    value: currentValue || []
                  }
                  
                  return (
                    <PropertyField
                      key={`${propName}-${previewMode}`}
                      propName={propName}
                      prop={propWithValue}
                      sectionName={getComponentId(component)}
                      onChange={(value) => handlePropertyChange(propName, value)}
                    />
                  )
                }

                // Determinar style a ser exibido conforme modo (string antiga ou responsiva)
                let styleForUi: string | undefined
                if (currentValue && typeof currentValue === 'object' && currentValue.style) {
                  if (typeof currentValue.style === 'string') {
                    styleForUi = currentValue.style
                  } else if (typeof currentValue.style === 'object') {
                    styleForUi = currentValue.style[previewMode] || ''
                  }
                }

                // Preservar o valor atual se existir, senão usar o padrão
                const isComplex = propType === 'date' || propType === 'select';
                const propWithValue = currentValue 
                  ? { ...prop, value: isComplex ? currentValue : (currentValue.value || currentValue), style: styleForUi, class: currentValue.class }
                  : { ...prop, value: prop.value || '' }
                
                return (
                  <PropertyField
                    key={`${propName}-${previewMode}`}
                    propName={propName}
                    prop={propWithValue}
                    sectionName={getComponentId(component)}
                    onChange={(value) => handlePropertyChange(propName, value)}
                  />
                )
              })}
            </div>
          </div>
        )}

        {(!component.props || Object.keys(component.props).length === 0) && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              Este componente não possui propriedades editáveis
            </p>
          </div>
        )}
      </div>

      {/* Botão Aplicar Mudanças - Fixo no final */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <button
          onClick={handleApplyChanges}
          className={`
            w-full px-4 py-3 rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200
            ${isClicked 
              ? 'bg-green-600 text-white scale-[0.98]' 
              : 'bg-gradient-primary text-white hover:opacity-90'
            }
          `}
        >
          <span className="text-xs">
            {isClicked ? 'Aplicando' : 'Aplicar Mudanças'}
          </span>
        </button>
        <p className="text-xs text-gray-500 text-center mt-2">
          Aplicar alterações no preview
        </p>
      </div>
    </div>
  );
}
